/**
 * Shared Auth Token Utility
 * 
 * Centralizes token retrieval across all API services.
 * Checks multiple localStorage keys for compatibility with different auth strategies.
 */

/**
 * Get the current authentication token from storage.
 * Checks multiple keys for compatibility with JWT adapter and legacy storage.
 * 
 * @returns {string|null} The auth token or null if not found
 */
export function getAuthToken() {
  if (typeof localStorage === 'undefined') return null;

  // Priority order:
  // 1. vh_auth_token (Firebase/JWT adapter primary key)
  // 2. access_token / accessToken (common OAuth/JWT keys)
  // 3. token / authToken (legacy keys)
  // 4. jwt (legacy key)
  return (
    localStorage.getItem('vh_auth_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('jwt') ||
    null
  );
}

/**
 * Build Authorization header object for authenticated requests.
 *
 * @returns {Record<string, string>} Authorization header or empty object
 */
export function getAuthHeader() {
  const token = getAuthToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

let tokenRefreshHandler = null;
let inFlightRefresh = null;

/**
 * Register the live auth adapter's refresh function (JWT adapter in local-dev).
 * Firebase strategy leaves this unset so getAuthTokenAsync freshness handling stays intact.
 *
 * @param {(() => Promise<string|null>)|null} fn
 */
export function registerTokenRefresh(fn) {
  tokenRefreshHandler = typeof fn === 'function' ? fn : null;
}

async function runRegisteredRefresh() {
  if (!tokenRefreshHandler) return null;
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = Promise.resolve()
    .then(() => tokenRefreshHandler())
    .finally(() => {
      inFlightRefresh = null;
    });
  return inFlightRefresh;
}

/**
 * Async token getter with Firebase fallback.
 * Uses stored token first; if missing and Firebase is configured, retrieves a fresh ID token.
 *
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<string|null>} The auth token or null if not found
 */
export async function getAuthTokenAsync({ forceRefresh = false } = {}) {
  const stored = getAuthToken();
  const storedPayload = decodeJwtPayload(stored);
  const storedIssuer = storedPayload?.iss;
  const storedIsFirebase = storedIssuer?.startsWith('https://securetoken.google.com/');
  const storedExpMs = storedPayload?.exp ? storedPayload.exp * 1000 : null;

  // For non-Firebase tokens (JWT adapter), return stored token if not expired
  if (stored && !storedIsFirebase) {
    if (forceRefresh) {
      const refreshed = await runRegisteredRefresh().catch(() => null);
      if (refreshed) return refreshed;
    }
    const isExpired = storedExpMs ? Date.now() >= storedExpMs : false;
    if (!isExpired) return stored;
    const refreshed = await runRegisteredRefresh().catch(() => null);
    if (refreshed) return refreshed;
  }

  // For Firebase tokens, check freshness (2-min buffer before expiry).
  // forceRefresh skips the buffer so a 401 can rotate an ID token that still
  // looks fresh locally.
  const isStoredFresh = storedIsFirebase && storedExpMs ? Date.now() < storedExpMs - 120000 : false;
  if (stored && storedIsFirebase && isStoredFresh && !forceRefresh) return stored;

  // Try Firebase refresh as fallback
  try {
    const { getFirebase } = await import('../firebase/lazy');
    const { auth } = await getFirebase();
    const user = auth?.currentUser;
    if (!user) return stored || null;
    const token = await user.getIdToken(true);
    if (token && typeof localStorage !== 'undefined') {
      localStorage.setItem('vh_auth_token', token);
      localStorage.setItem('access_token', token);
      localStorage.setItem('vh_auth_strategy', 'firebase');
    }
    return token || stored || null;
  } catch {
    return stored || null;
  }
}

/**
 * Async Authorization header builder.
 * 
 * @returns {Promise<Record<string, string>>} Authorization header or empty object
 */
export async function getAuthHeaderAsync() {
  const token = await getAuthTokenAsync();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Decode JWT payload without verifying signature.
 *
 * @param {string|null} token
 * @returns {object|null} Decoded payload or null
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payload = parts[1];
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    if (typeof atob === 'function') {
      return JSON.parse(atob(padded));
    }
    if (typeof Buffer !== 'undefined') {
      return JSON.parse(Buffer.from(padded, 'base64').toString('utf-8'));
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Get the current refresh token from storage.
 * 
 * @returns {string|null} The refresh token or null if not found
 */
export function getRefreshToken() {
  if (typeof localStorage === 'undefined') return null;
  
  return (
    localStorage.getItem('vh_refresh_token') ||
    localStorage.getItem('refresh_token') ||
    null
  );
}

/**
 * Check if user is currently authenticated (has a token)
 * 
 * @returns {boolean} True if an auth token exists
 */
export function isAuthenticated() {
  return !!getAuthToken();
}

/**
 * Clear all authentication tokens from storage.
 * Used during logout to ensure clean state.
 */
export function clearAuthTokens() {
  if (typeof localStorage === 'undefined') return;
  
  // Clear all possible token keys
  localStorage.removeItem('vh_auth_token');
  localStorage.removeItem('vh_refresh_token');
  localStorage.removeItem('vh_user_data');
  localStorage.removeItem('access_token');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('jwt');
  localStorage.removeItem('vh_auth_strategy');
}

export default {
  getAuthToken,
  getAuthTokenAsync,
  getAuthHeader,
  getAuthHeaderAsync,
  decodeJwtPayload,
  getRefreshToken,
  isAuthenticated,
  clearAuthTokens,
  registerTokenRefresh,
};
